import { createUploadLink } from 'apollo-upload-client';
import { getAccessToken, logoutCleanup } from 'helpers/auth';
import {
  ApolloClient,
  ApolloLink,
  from,
  fromPromise,
  HttpLink,
  Operation,
  RequestHandler,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import cache from './cache';
import { requestTokenRefresh } from './refreshToken';

// Add token refresh lock
export let tokenRefreshPromise: Promise<boolean> | null = null;

export const errorLink = onError(
  ({ graphQLErrors, networkError, forward, operation }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // eslint-disable-next-line no-console
        console.log(
          `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`
        );
        if (
          ((err.extensions?.response as { statusCode: number })?.statusCode ===
            401 ||
            (err.extensions?.originalError as { statusCode: number })
              ?.statusCode === 401) &&
          operation.operationName !== 'RequestTokenRefresh'
        ) {
          // If there's already a refresh in progress, use that promise
          if (!tokenRefreshPromise) {
            tokenRefreshPromise = requestTokenRefresh(client)
              // eslint-disable-next-line
              .then(result => {
                tokenRefreshPromise = null;
                return result;
              })
              // eslint-disable-next-line
              .catch(error => {
                tokenRefreshPromise = null;
                throw error;
              });
          }

          return fromPromise(tokenRefreshPromise)
            .filter(value => Boolean(value))
            .flatMap(() => {
              return forward(operation);
            });
        }

        if (err.message === 'Forbidden') {
          logoutCleanup();
        }
      }
    }

    if (networkError) {
      // eslint-disable-next-line no-console
      console.log(`[Network error]: ${networkError}`);
    }

    return undefined;
  }
);

const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();
  operation.setContext({
    headers: {
      Authorization: token && `Bearer ${token}`,
      'X-Client': 'admin',
    },
  });

  return forward(operation);
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_BASE_URL as string,
});

const uploadLink = createUploadLink({
  uri: import.meta.env.VITE_MONOLITH_API_URL,
  headers: {
    'apollo-require-preflight': 'true',
    'keep-alive': 'true',
    Authorization: `Bearer ${getAccessToken()}`,
  },
});

const isUploadOperation = (operation: Operation) =>
  !!operation.getContext().isUpload;

const terminatingLink = split(
  isUploadOperation,
  uploadLink as unknown as ApolloLink | RequestHandler,
  httpLink
);

const client = new ApolloClient({
  cache,
  link: from([errorLink, authLink, terminatingLink]),
});

export default client;
